import { games, davesPicksGameIds, getGameColor } from '../data/games';
import { useAuth } from '../contexts/AuthContext';
import { usePrefs } from '../contexts/PrefsContext';
import { usePlay } from '../contexts/PlayContext';
import GameListing from './GameListing';
import SectionBlock from './SectionBlock';
import HeartIcon from './HeartIcon';
import DismissIcon from './DismissIcon';
import { realFavoritesCount } from '../utils/pageBreakUtils';
import { buildSuggestions } from '../utils/suggestPicks';

// Picks a stable "game of the day" from Dave's Picks - the same game all day,
// for everyone, rotating to the next pick the following day.
const getTodaysPickId = () => {
  if (!davesPicksGameIds.length) return null;
  const dayIndex = Math.floor(Date.now() / 86400000);
  return davesPicksGameIds[dayIndex % davesPicksGameIds.length];
};

// Editor's Picks, optionally under the daily hero tile.
//
// The hero was removed in Phase 4 (D43) and brought back in Phase 6, for
// ladder states 0-4 only - `showHero` comes from Home. Note that's a partial
// reversal: D43 said if it returned it should return for *everyone*, on the
// grounds that a shared event only newcomers can see isn't shared. It now
// returns for everyone except state 5, who are the people already sharing.
//
// The pick itself is unaffected by any personalisation - same game, same day,
// every user - which is the one property that makes it worth the space.
function FeaturedSection({ showHero = false }) {
  const { user } = useAuth();
  const {
    favorites,
    addFavorite,
    removeFavorite,
    unlikedGames,
    unlikeGame
  } = usePrefs();
  const { gamesPlayedToday, handleGameLinkClick } = usePlay();

  const toggleFavorite = (id, newValue) => {
    if (newValue) addFavorite(id);
    else removeFavorite(id);
  };

  const todaysPickId = showHero ? getTodaysPickId() : null;

  // Dismissing the hero hides it outright, the same as dismissing a row hides
  // that row - the ✕ means "not interested, stop suggesting this", and a
  // dismissed game that stayed on screen all day would mean the opposite.
  // Note `unlikedGames` is live while the ladder state is latched, so this
  // takes effect immediately on click rather than waiting for a reload.
  const heroGame = todaysPickId && !unlikedGames.includes(todaysPickId)
    ? games.find(g => g.id === todaysPickId)
    : null;

  // Most of this list is drawn from the categories the user already favors -
  // see utils/suggestPicks.js. The hero's game is excluded so the same tile
  // isn't shown twice, but only when there *is* a hero.
  const morePicks = buildSuggestions({
    poolIds: davesPicksGameIds,
    favorites,
    unliked: unlikedGames,
    excludeId: todaysPickId,
  }).map(id => games.find(g => g.id === id));

  if (!heroGame && morePicks.length === 0) return null;

  const heroColor = heroGame ? getGameColor(heroGame) : null;
  const heroIsFavorite = heroGame ? favorites.includes(heroGame.id) : false;
  const suggestedTitle = heroGame ? "Suggested for You" : "Suggested Games"

  return (
    <>
      {heroGame && (
        <SectionBlock title="Featured Today">
          {/* The buttons are siblings of the link, not children of it: a
              <button> inside an <a> is invalid, and nesting them would make
              every dismissal also open the game. */}
          <div className="relative">
            <a
              href={heroGame.url}
              onClick={(e) => { e.preventDefault(); handleGameLinkClick(heroGame); }}
              className="block relative overflow-hidden px-4 py-3 hover:bg-white/[0.03] transition-colors duration-150"
            >
              <div
                className="absolute inset-1 rounded-xl pointer-events-none"
                style={{ backgroundImage: `linear-gradient(45deg, color-mix(in srgb, ${heroColor} 17.6%, transparent), transparent 48.75%), linear-gradient(225deg, color-mix(in srgb, ${heroColor} 9.6%, transparent), transparent 30%)` }}
              />
              <div className="relative flex items-start gap-4">
                <img src={"/img/games/" + heroGame.image} alt={heroGame.name} className="w-[75px] h-[75px] rounded-2xl border-2 border-black flex-shrink-0" />
                <div className="min-w-0 flex-1 text-left">
                  {/* pr-16 keeps the title clear of the two buttons above it. */}
                  <h3 className="font-display font-black text-2xl sm:text-3xl leading-tight mb-1 pr-16" style={{ color: heroColor }}>{heroGame.name}</h3>
                  <p className="text-sm text-nlg-muted leading-snug">{heroGame.caption}</p>
                </div>
              </div>
              <div className="relative mt-2 flex justify-end">
                <span
                  className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold text-nlg-bg transition-transform duration-150 hover:scale-[1.03]"
                  style={{ backgroundColor: heroColor }}
                >
                  Play Now →
                </span>
              </div>
            </a>

            {/* Same pair as an Editor's Picks row, same sizes and accent
                treatment: ✕ outermost, heart inside it. */}
            {/* Each button is a transparent 36x44px target around the 28px
                circle (the inner span). 36px wide is exactly the circles'
                centre-to-centre spacing, so the two targets tile without
                overlapping; the offsets keep the circles where they were. */}
            <div className="absolute top-0 right-1 flex items-center">
              <button
                onClick={(e) => { e.preventDefault(); toggleFavorite(heroGame.id, !heroIsFavorite); }}
                className="group w-9 h-11 flex items-center justify-center"
                aria-label={heroIsFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 group-hover:scale-110"
                  style={{
                    borderColor: heroColor,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    ...(heroIsFavorite ? { backgroundColor: `color-mix(in srgb, ${heroColor} 18%, transparent)` } : {})
                  }}
                >
                  <HeartIcon filled={heroIsFavorite} color={heroColor} className="w-[13.5px] h-[13.5px]" />
                </span>
              </button>

              <button
                onClick={(e) => { e.preventDefault(); unlikeGame(heroGame.id); }}
                className="group w-9 h-11 flex items-center justify-center"
                aria-label="Not interested, hide today's featured game"
                title="Not interested"
              >
                <span
                  className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 group-hover:scale-110"
                  style={{ borderColor: heroColor, borderWidth: 1, borderStyle: 'solid' }}
                >
                  <DismissIcon color={heroColor} className="w-[16.5px] h-[16.5px]" />
                </span>
              </button>
            </div>
          </div>
        </SectionBlock>
      )}

      {morePicks.length > 0 && (
    <SectionBlock title={suggestedTitle}>
      {/* Hidden for a visitor with no record yet (no token written) - there's
          nothing to explain about buttons that haven't done anything until
          a first action creates one, and showing it any earlier would be
          explaining a page state the visitor hasn't reached. */}
      {user && (realFavoritesCount(favorites) === 0 || unlikedGames.length === 0) && (
        <p
          className="p-2 m-1 rounded-lg text-sm text-nlg-muted overflow-hidden"
          style={{ backgroundColor: 'color-mix(in srgb, #1a1a2e 100%, white 8%)' }}
        >
          <img
            src="/img/love.gif"
            alt=""
            className="w-[50px] h-[50px] rounded-lg border-2 border-black float-right ml-3"
          />
          Click {' '}
          <HeartIcon color="currentColor" className="align-middle mx-[2px] w-4 h-4 text-nlg-gold" />{' '}
          to add a game to your daily playlist, 
          <br/>Click <DismissIcon className="align-middle mx-[2px] w-4 h-4 text-nlg-gold" /> to dismiss it and see a new one.
        </p>
      )}

      <ul className="list-none">
        {morePicks.map(game => (
          <GameListing
            key={game.id}
            game={game}
            isFavorite={favorites.includes(game.id)}
            toggleFavorite={toggleFavorite}
            onGameLinkClick={handleGameLinkClick}
            dimGame={gamesPlayedToday.includes(game.id)}
            onUnlike={unlikeGame}
          />
        ))}
      </ul>
        </SectionBlock>
      )}
    </>
  );
}

export default FeaturedSection;
